import React, { useMemo } from 'react';
import { EChart } from '@/components/visuals';
import type { ChartDensity } from '@/components/visuals';
import type { EChartsOption } from 'echarts';

export interface CriticalityChartProps {
  critical: number;
  nearCritical: number;
  monitor: number;
  height: number | string;
  onSegmentClick: (band: 'critical' | 'near-critical' | 'monitor') => void;
  density?: ChartDensity;
}

export function CriticalityChart({
  critical,
  nearCritical,
  monitor,
  height,
  onSegmentClick,
  density = 'overview',
}: CriticalityChartProps) {
  const option = useMemo<EChartsOption>(() => ({
    tooltip: density === 'preview' ? { show: false } : { trigger: 'item' },
    legend:
      density === 'preview'
        ? { bottom: 0, left: 'center', data: ['Critical', 'Near-critical', 'Monitor'], textStyle: { fontSize: 9 }, itemGap: 6 }
        : { bottom: 0, left: 'center', data: ['Critical', 'Near-critical', 'Monitor'] },
    series: [{
      type: 'pie',
      radius: ['35%', '65%'],
      center: ['50%', '50%'],
      avoidLabelOverlap: false,
      itemStyle: { borderColor: 'var(--card)', borderWidth: 2 },
      label: {
        show: density !== 'preview',
        formatter: '{b}: {c}',
        fontSize: density === 'focus' ? 11 : 10,
      },
      data: [
        { value: critical, name: 'Critical', itemStyle: { color: '#EF4444' } },
        { value: nearCritical, name: 'Near-critical', itemStyle: { color: '#F59E0B' } },
        { value: monitor, name: 'Monitor', itemStyle: { color: '#3B82F6' } },
      ],
    }],
  }), [critical, nearCritical, monitor, density]);

  const onEvents = useMemo(() => ({
    click: (params: { name?: string }) => {
      const name = params.name?.toLowerCase();
      if (name === 'critical') onSegmentClick('critical');
      else if (name === 'near-critical') onSegmentClick('near-critical');
      else if (name === 'monitor') onSegmentClick('monitor');
    },
  }), [onSegmentClick]);

  return <EChart option={option} height={height} onEvents={onEvents} />;
}
